'use client'

import { useParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

const MAESTRO_HASH_HISTORY_ENTRY_KEY = '__maestroHashHistoryEntry'
const MAESTRO_HASH_CHANGE_EVENT = 'maestro:hashchange'

let isHashBackPending = false

const getHash = () =>
  typeof window !== 'undefined' ? window.location.hash.replace('#', '') : ''

const toMap = (val: string) => {
  // Treat duplicate hash separators as param separators (e.g. filters#filters).
  const hashParams = val.split(/[&#]/).filter((s) => s !== '')
  const res = new Map<string, string>()
  hashParams.forEach((v) => {
    const keyValue = v.split('=')
    const len = keyValue.length
    if (len == 0) {
      return
    }
    if (len == 1) {
      res.set(keyValue[0], '')
      return
    }
    res.set(keyValue[0], keyValue[1])
  })
  return res
}

const fromMap = (hashMap: Map<string, string>) => {
  let res = [] as string[]
  hashMap.forEach((value, key) => {
    if (value === '') {
      res = res.concat(key)
      return
    }
    res = res.concat(`${key}=${value}`)
  })
  return res.join('&')
}

const updateUrl = (newHashMap: Map<string, string>, replace = true) => {
  const newHash = fromMap(newHashMap)
  if (typeof window === 'undefined') {
    return
  }

  const currentState =
    typeof window.history.state === 'object' && window.history.state !== null
      ? (window.history.state as Record<string, unknown>)
      : {}

  const newPath = `${window.location.pathname}${window.location.search}${newHash ? `#${newHash}` : ''}`
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`

  if (newPath === currentPath) {
    return
  }

  const shouldNavigateBack =
    replace && Boolean(currentState[MAESTRO_HASH_HISTORY_ENTRY_KEY])

  if (shouldNavigateBack) {
    if (isHashBackPending) {
      return
    }

    isHashBackPending = true
    let fallbackTimeout: ReturnType<typeof setTimeout> | undefined = undefined
    function releaseBackPending() {
      isHashBackPending = false
      if (fallbackTimeout !== undefined) {
        clearTimeout(fallbackTimeout)
      }
    }

    window.addEventListener('popstate', releaseBackPending, { once: true })
    // Fallback in case popstate is not emitted by the environment.
    fallbackTimeout = setTimeout(() => {
      window.removeEventListener('popstate', releaseBackPending)
      releaseBackPending()
    }, 1000)
    window.history.back()
    return
  }

  if (replace) {
    window.history.replaceState(currentState, '', newPath)
  } else {
    window.history.pushState(
      {
        ...currentState,
        [MAESTRO_HASH_HISTORY_ENTRY_KEY]: true,
      },
      '',
      newPath
    )
  }

  window.dispatchEvent(new CustomEvent(MAESTRO_HASH_CHANGE_EVENT))
}

export const useHash = () => {
  const params = useParams()
  const [hash, setHash] = useState(getHash)

  useEffect(() => {
    setHash(getHash())
  }, [params])

  useEffect(() => {
    const handler = () => setHash(getHash())
    window.addEventListener(MAESTRO_HASH_CHANGE_EVENT, handler)
    window.addEventListener('popstate', handler)
    return () => {
      window.removeEventListener(MAESTRO_HASH_CHANGE_EVENT, handler)
      window.removeEventListener('popstate', handler)
    }
  }, [])

  const applyHashOperation = useCallback(
    (operation: (map: Map<string, string>) => void, replace = false) => {
      const currentHashStr = getHash()
      const hashMap = toMap(currentHashStr)
      operation(hashMap)
      updateUrl(hashMap, replace)
    },
    []
  )

  const addOrUpdateHashParam = useCallback(
    (param: string, value = '', replace = false) => {
      applyHashOperation((map) => {
        map.set(param, value)
      }, replace)
    },
    [applyHashOperation]
  )

  const removeHashParam = useCallback(
    (param: string) => {
      applyHashOperation((map) => {
        map.delete(param)
      }, true)
    },
    [applyHashOperation]
  )

  const popHash = useCallback(() => {
    applyHashOperation((map) => {
      const keys = Array.from(map.keys())
      if (keys.length > 0) {
        map.delete(keys[keys.length - 1])
      }
    }, true)
  }, [applyHashOperation])

  const clearHash = useCallback(() => {
    applyHashOperation((map) => {
      map.clear()
    }, true)
  }, [applyHashOperation])

  const getHashParamValue = useCallback(
    (param: string) => {
      return toMap(hash).get(param)
    },
    [hash]
  )

  return {
    hash,
    addOrUpdateHashParam,
    removeHashParam,
    getHashParamValue,
    popHash,
    clearHash,
  }
}
